<script>
export const reuseComponent = true
</script>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN

const props = defineProps({
  item: Object,
})

const mapContainer = ref(null)
const map = ref(null)
const markersMap = ref(new Map())
const markerPinsMap = ref(new Map())

const locations = computed(() => {
  const enrichedLocations = Object.fromEntries((props.item?.enrichment?.locations || []).map((location) => [location.uuid, location]))

  return (props.item?.properties?.darioLiveLocations ?? [])
    ?.map((location) => ({
      ...location,
      dario: enrichedLocations[location.dario.uuid],
    }))
    .filter((location) => {
      const { long, lat } = location.dario?.active_tracker?.most_recent_location ?? { lat: null, long: null }
      return long && lat
    })
})

const topMostPinSize = computed(() => {
  const validLocations = locations.value.filter((location) => !!location.pin?.size)

  if (!validLocations.length) return null

  const northernmostLocation = validLocations.reduce((mostNorthern, current) => {
    const currentLat = parseFloat(current.dario?.active_tracker?.most_recent_location?.lat || 0)
    const mostNorthernLat = parseFloat(mostNorthern.dario?.active_tracker?.most_recent_location?.lat || 0)

    return currentLat > mostNorthernLat ? current : mostNorthern
  }, validLocations[0])

  return northernmostLocation?.pin?.size || 0
})

const zoom = computed(() => parseInt(props.item.properties.zoomLevel, 10))
const mapStyle = computed(() => props.item.properties.mapStyle)
const paddingValue = computed(() => parseInt(props.item.properties.paddingValue, 10))
const animationDuration = computed(() => parseInt(props.item.properties.animationDuration, 10))

const createMarkerElement = (pin) => {
  if (!pin || !pin.image) return null

  const el = document.createElement('div')
  el.style.backgroundImage = `url(${pin.image})`
  el.style.height = `${pin.size}px`

  return el
}

const calculateBounds = () => {
  const bounds = new mapboxgl.LngLatBounds()
  let hasValidMarkers = false

  locations.value.forEach((location) => {
    const { long, lat } = location.dario.active_tracker.most_recent_location
    if (long && lat) {
      const coordinates = [parseFloat(long), parseFloat(lat)]
      bounds.extend(coordinates)
      hasValidMarkers = true
    }
  })

  return { bounds, hasValidMarkers }
}

const fitMapToBounds = (useAnimation = true) => {
  if (!map.value) return

  const { hasValidMarkers, bounds } = calculateBounds()

  if (!hasValidMarkers) return

  map.value.fitBounds(bounds, {
    padding: {
      top: paddingValue.value + topMostPinSize.value,
      bottom: paddingValue.value,
      left: paddingValue.value,
      right: paddingValue.value,
    },
    maxZoom: zoom.value,
    duration: useAnimation ? animationDuration.value : 0,
  })
}

const updateMarkers = () => {
  if (!map.value) return

  const currentLocationIds = new Set(locations.value.map((location) => location.dario.uuid))
  const existingMarkerIds = new Set(markersMap.value.keys())

  const markersToUpdate = new Set()
  const markersToCreate = new Set()
  const markersToRemove = new Set()

  locations.value.forEach((location) => {
    const locationId = location.dario?.uuid

    const currentPin = location.pin || {}
    const previousPin = markerPinsMap.value.get(locationId) || {}
    const pinChanged = currentPin.image !== previousPin.image || currentPin.size !== previousPin.size

    if (existingMarkerIds.has(locationId)) {
      if (pinChanged) {
        markersToRemove.add(locationId)
        markersToCreate.add(locationId)
      } else {
        markersToUpdate.add(locationId)
      }
    } else {
      markersToCreate.add(locationId)
    }
  })

  existingMarkerIds.forEach((id) => {
    if (!currentLocationIds.has(id)) {
      markersToRemove.add(id)
    }
  })

  for (const locationId of markersToRemove) {
    markersMap.value.get(locationId).remove()
    markersMap.value.delete(locationId)
    markerPinsMap.value.delete(locationId)
  }

  locations.value.forEach((location) => {
    const locationId = location.dario?.uuid
    const { long, lat } = location.dario.active_tracker.most_recent_location
    const coordinates = [parseFloat(long), parseFloat(lat)]

    if (markersToUpdate.has(locationId)) {
      markersMap.value.get(locationId).setLngLat(coordinates)
      return
    }

    if (markersToCreate.has(locationId)) {
      const marker = new mapboxgl.Marker({
        element: createMarkerElement(location.pin),
        anchor: 'bottom',
      })
        .setLngLat(coordinates)
        .addTo(map.value)

      markersMap.value.set(locationId, marker)
      markerPinsMap.value.set(locationId, { ...location.pin })
    }
  })

  fitMapToBounds()
}

onMounted(() => {
  if (!mapContainer.value) return

  map.value = new mapboxgl.Map({
    container: mapContainer.value,
    style: mapStyle.value,
    zoom: zoom.value,
    attributionControl: false,
  })

  map.value.on('load', () => {
    fitMapToBounds(false)
    updateMarkers()
  })

  return () => {
    for (const marker of markersMap.value.values()) {
      marker.remove()
    }
    markersMap.value.clear()
    markerPinsMap.value.clear()

    if (map.value) map.value.remove()
  }
})

watch(
  locations,
  () => {
    updateMarkers()
  },
  { deep: true }
)

watch(
  () => props.item.properties.position,
  () => {
    if (!map.value) return

    nextTick(() => {
      map.value.resize()
      updateMarkers()
    })
  },
  { deep: true }
)
</script>

<template>
  <div
    class="absolute overflow-hidden"
    :style="{
      left: `${item.properties.position.xLeft}px`,
      top: `${item.properties.position.yTop}px`,
      width: `${item.properties.position.width}px`,
      height: `${item.properties.position.height}px`,
    }"
  >
    <div ref="mapContainer" class="absolute inset-0 w-full h-full" />
  </div>
</template>

<style scoped>
:deep(.mapboxgl-marker) {
  width: 100%;
  background-size: contain;
  background-repeat: no-repeat;
  background-position: bottom center;
}
</style>
